var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * AccumulationChart file
 */
import { Property, Component, Complex, Collection, NotifyPropertyChanges, animationMode, SanitizeHtmlHelper } from '@syncfusion/ej2-base';
import { Internationalization, Event, Browser, EventHandler, Touch } from '@syncfusion/ej2-base';
import { remove, extend, isNullOrUndefined } from '@syncfusion/ej2-base';
import { Margin, Border, TooltipSettings, CenterLabel, Indexes, Accessibility, TitleStyleSettings } from '../common/model/base';
import { AccumulationSeries, PieCenter } from './model/acc-base';
import { getThemeColor } from '../common/model/theme';
import { load, pointClick } from '../common/model/constants';
import { pointMove, chartDoubleClick, chartMouseClick, chartMouseDown } from '../common/model/constants';
import { chartMouseLeave, chartMouseMove, chartMouseUp, resized, beforeResize } from '../common/model/constants';
import { LegendSettings } from '../common/legend/legend';
import { indexFinder, appendChildElement, redrawElement, getTextAnchor, stringToNumber, textWrap, subtractRect, lightenColor, brightenColor, normalizeGradientStops, removePreviousGradient, sanitizeGradientColor, getOrCreateSvgDefs } from '../common/utils/helper';
import { RectOption, showTooltip, ImageOption } from '../common/utils/helper';
import { textElement, createSvg, calculateSize, removeElement, firstToLowerCase, withInBounds } from '../common/utils/helper';
import { getElement, titlePositionX } from '../common/utils/helper';
import { Rect, Size, measureText, TextOption } from '@syncfusion/ej2-svg-base';
import { Data } from '../common/model/data';
import { AccumulationBase } from './renderer/accumulation-base';
import { PieSeries } from './renderer/pie-series';
import { AccumulationAnnotationSettings } from './model/acc-base';
import { getTitle, AccPointData } from '../common/utils/helper';
import { Animation } from '@syncfusion/ej2-base';
import { PrintUtils } from '../common/utils/print';
import { createTemplate } from '../common/utils/helper';
import { createElement } from '@syncfusion/ej2-base';
/**
 * Represents the AccumulationChart control.
 * ```html
 * <div id="accumulation"/>
 * <script>
 *   var accObj = new AccumulationChart({});
 *   accObj.appendTo("#accumulation");
 * </script>
 * ```
 *
 * @public
 */
let AccumulationChart = class AccumulationChart extends Component {
    /**
     * Constructor for creating the AccumulationChart widget.
     *
     * @private
     * @param {AccumulationChartModel} options - Specifies the accumulation chart model.
     * @param {string | HTMLElement} element - Specifies the element for the accumulation chart.
     */
    constructor(options, element) {
        super(options, element);
        /** @private */
        this.animateselected = false;
        /** @private */
        this.explodeDistance = 0;
        /** @private */
        this.currentLegendIndex = 0;
        /** @private */
        this.currentPointIndex = 0;
        /** @private */
        this.previousTargetId = '';
        /** @private */
        this.isLegendClicked = false;
        this.chartid = 57724;
    }
    /**
     * Animate the series bounds on data change.
     *
     * @private
     * @param {number} duration - The duration of the animation.
     * @returns {void}
     */
    animate(duration) {
        this.duration = (duration === 0 && animationMode === 'Enable') ? 700 : duration;
        this.animateselected = true;
        this.animateSeries = false;
        let temIndex = 0;
        const tempcolor = [];
        const tempindex = [];
        const tempindex1 = [];
        const currentSeries = this.visibleSeries[0];
        let datasource = [];
        datasource = currentSeries.dataSource;
        currentSeries.sumOfPoints = 0;
        if (currentSeries.points.length < Object.keys(currentSeries.dataSource).length) {
            this.refresh();
        }
        else if (currentSeries.points.length > Object.keys(currentSeries.dataSource).length) {
            const currentSeries = this.visibleSeries[0];
            currentSeries.points = currentSeries.points.filter((entry1) => {
                entry1.visible = false;
                tempindex.push(entry1.index);
                tempcolor.push(entry1.color);
                return (datasource).some((entry2) => {
                    const accPoint = entry2;
                    if (entry1.x === accPoint.x) {
                        entry1.visible = true;
                        tempindex1.push(entry1.index);
                        entry1.index = temIndex;
                        temIndex++;
                    }
                    return entry1.x === accPoint.x;
                });
            });
            const missing = tempindex.filter((item) => tempindex1.indexOf(item) < 0);
            const interval = tempindex.length - missing.length;
            for (let i = (tempindex.length - 1); i >= interval; i--) {
                removeElement('container_Series_0_Point_' + tempindex[i]);
            }
            for (let i = 0; i < currentSeries.points.length; i++) {
                currentSeries.points[i].y = currentSeries.dataSource[i].y;
                currentSeries.points[i].color = tempcolor[i];
                currentSeries.sumOfPoints += currentSeries.dataSource[i].y;
            }
            this.redraw = this.enableAnimation;
            this.animateSeries = false;
            this.calculateBounds();
            this.renderElements();
        }
        else {
            for (let i = 0; i < currentSeries.points.length; i++) {
                currentSeries.points[i].y = currentSeries.dataSource[i][currentSeries.yName];
                currentSeries.points[i].color = currentSeries.dataSource[i][currentSeries.pointColorMapping] != null
                    ? currentSeries.dataSource[i][currentSeries.pointColorMapping] : currentSeries.points[i].color;
                currentSeries.sumOfPoints += currentSeries.dataSource[i][currentSeries.yName];
            }
            this.redraw = this.enableAnimation;
            this.animateSeries = false;
            this.removeSvg();
            this.refreshPoints(currentSeries.points);
            this.renderElements();
        }
    }
    /**
     * Gets the type of accumulation chart.
     *
     * @returns {AccumulationType} - The type of accumulation chart.
     * @private
     * */
    get type() {
        if (this.series && this.series.length) {
            return this.series[0].type;
        }
        return 'Pie';
    }
    // accumulation chart methods.
    /**
     * To create svg object, renderer and binding events for the container.
     *
     * @returns {void}
     */
    preRender() {
        this.allowServerDataBinding = false;
        this.unWireEvents();
        this.setCulture();
        this.animateSeries = true;
        if (this.element.id === '') {
            let collection = document.getElementsByClassName('e-accumulationchart').length;
            let elementid = 'acc_chart_' + this.chartid + '_' + collection;
            while (document.getElementById(elementid)) {
                collection++;
                elementid = 'acc_chart_' + this.chartid + '_' + collection;
            }
            this.element.id = 'acc_chart_' + this.chartid + '_' + collection;
        }
        this.wireEvents();
        this.element.setAttribute('dir', this.enableRtl ? 'rtl' : 'ltr');
        this.element.style.outline = 'none';
    }
    /**
     * Themeing for chart goes here.
     *
     * @returns {void}
     */
    setTheme() {
        /** Set theme for accumulation chart */
        this.themeStyle = getThemeColor(this.theme, false, this);
    }
    /**
     * To render the accumulation chart elements.
     *
     * @returns {void}
     */
    render() {
        if (this.element.className.indexOf('e-accumulationchart') === -1) {
            this.element.classList.add('e-accumulationchart');
        }
        this.element.setAttribute('role', this.accessibility.accessibilityRole ? this.accessibility.accessibilityRole : 'region');
        this.element.setAttribute('tabindex', this.accessibility.focusable ? String(this.accessibility.tabIndex) : '-1');
        this.element.setAttribute('aria-label', this.accessibility.accessibilityDescription ? this.accessibility.accessibilityDescription : this.title + '. Syncfusion interactive chart.');
        this.element.setAttribute('class', this.element.getAttribute('class') + ' e-accumulationchart-focused');
        const loadEventData = {
            chart: this,
            accumulation: this,
            theme: this.theme, name: load, cancel: false
        };
        this.trigger(load, loadEventData, () => {
            this.setTheme();
            this.accBaseModule = new AccumulationBase(this);
            this.pieSeriesModule = new PieSeries(this);
            this.calculateVisibleSeries();
            this.processData();
            this.renderComplete();
            this.allowServerDataBinding = true;
        });
    }
    /**
     * Method to unbind events for accumulation chart.
     *
     * @returns {void}
     */
    unWireEvents() {
        /** Find the Events type */
        const isIE11Pointer = Browser.isPointer;
        const start = Browser.touchStartEvent;
        const move = Browser.touchMoveEvent;
        const stop = Browser.touchEndEvent;
        const cancel = isIE11Pointer ? 'pointerleave' : 'mouseleave';
        /** UnBind the Event handler */
        EventHandler.remove(this.element, move, this.accumulationMouseMove);
        EventHandler.remove(this.element, stop, this.accumulationMouseEnd);
        EventHandler.remove(this.element, start, this.accumulationMouseStart);
        EventHandler.remove(this.element, 'click', this.accumulationOnMouseClick);
        EventHandler.remove(this.element, 'dblclick', this.accumulationOnDoubleClick);
        EventHandler.remove(this.element, 'contextmenu', this.accumulationRightClick);
        EventHandler.remove(this.element, cancel, this.accumulationMouseLeave);
        EventHandler.remove(this.element, 'keydown', this.accumulationChartKeyDown);
        EventHandler.remove(document.body, 'keydown', this.documentKeyHandler);
        EventHandler.remove(this.element, 'keyup', this.accumulationChartKeyUp);
        window.removeEventListener((Browser.isTouch && ('orientation' in window && 'onorientationchange' in window)) ? 'orientationchange' : 'resize', this.accumulationResizeBound);
    }
    /**
     * Method to bind events for the accumulation chart.
     *
     * @returns {void}
     */
    wireEvents() {
        /**
         * To fix react timeout destroy issue.
         */
        if (!this.element) {
            return;
        }
        /** Find the Events type */
        const isIE11Pointer = Browser.isPointer;
        const start = Browser.touchStartEvent;
        const stop = Browser.touchEndEvent;
        const move = Browser.touchMoveEvent;
        const cancel = isIE11Pointer ? 'pointerleave' : 'mouseleave';
        /** Bind the Event handler */
        EventHandler.add(this.element, move, this.accumulationMouseMove, this);
        EventHandler.add(this.element, stop, this.accumulationMouseEnd, this);
        EventHandler.add(this.element, start, this.accumulationMouseStart, this);
        EventHandler.add(this.element, 'click', this.accumulationOnMouseClick, this);
        EventHandler.add(this.element, 'dblclick', this.accumulationOnDoubleClick, this);
        EventHandler.add(this.element, 'contextmenu', this.accumulationRightClick, this);
        EventHandler.add(this.element, cancel, this.accumulationMouseLeave, this);
        EventHandler.add(this.element, 'keydown', this.accumulationChartKeyDown, this);
        EventHandler.add(document.body, 'keydown', this.documentKeyHandler, this);
        EventHandler.add(this.element, 'keyup', this.accumulationChartKeyUp, this);
        this.accumulationResizeBound = this.accumulationResize.bind(this);
        window.addEventListener((Browser.isTouch && ('orientation' in window && 'onorientationchange' in window)) ? 'orientationchange' : 'resize', this.accumulationResizeBound);
        new Touch(this.element); // To avoid geasture blocking for browser
        /** Apply the style for chart */
        this.setStyle(this.element);
    }
    /**
     * Method to set mouse x, y from events.
     *
     * @param {PointerEvent} e - The pointer event containing mouse coordinates.
     * @returns {void}
     */
    setMouseXY(e) {
        let pageX;
        let pageY;
        const svgRectElement = getElement(this.element.id + '_svg');
        if (svgRectElement && this.element) {
            const svgRect = svgRectElement.getBoundingClientRect();
            const rect = this.element.getBoundingClientRect();
            if (e.type.indexOf('touch') > -1) {
                this.isTouch = true;
                const touchArg = e;
                pageY = touchArg.changedTouches[0].clientY;
                pageX = touchArg.changedTouches[0].clientX;
            }
            else {
                this.isTouch = e.pointerType === 'touch' || e.pointerType === '2';
                pageX = e.clientX;
                pageY = e.clientY;
            }
            this.mouseY = (pageY - rect.top) - Math.max(svgRect.top - rect.top, 0);
            this.mouseX = (pageX - rect.left) - Math.max(svgRect.left - rect.left, 0);
        }
    }
    /**
     * Handles the mouse end.
     *
     * @param {PointerEvent} e - The pointer event containing mouse coordinates.
     * @returns {boolean} - Mouse end of accumulation chart.
     * @private
     */
    accumulationMouseEnd(e) {
        this.setMouseXY(e);
        this.trigger(chartMouseUp, { target: e.target.id, x: this.mouseX, y: this.mouseY });
        if (this.isTouch) {
            this.titleTooltip(e, this.mouseX, this.mouseY, this.isTouch);
            if (this.accumulationDataLabelModule && this.visibleSeries[0].dataLabel.visible) {
                this.accumulationDataLabelModule.move(e, this.mouseX, this.mouseY, this.isTouch);
            }
            if (this.accumulationLegendModule && this.legendSettings.visible) {
                this.accumulationLegendModule.move(e);
            }
        }
        if (this.centerLabel.hoverTextFormat) {
            this.updateCenterLabel(e);
        }
        this.notify(Browser.touchEndEvent, e);
        return false;
    }
    /*public removeSvgOffset(x: number, y: number): ChartLocation {
        let rect: ClientRect = this.element.getBoundingClientRect();
        let svgRect: ClientRect = getElement(this.element.id + '_svg').getBoundingClientRect();
        return { x: (x - rect.left) - Math.max(svgRect.left - rect.left, 0), y: (y - rect.top) - Math.max(svgRect.top - rect.top, 0)};
    }*/
    /**
     * Handles the mouse start.
     *
     * @param {PointerEvent} e - The pointer event containing mouse coordinates.
     * @returns {boolean} - Mouse start of accumulation chart.
     * @private
     */
    accumulationMouseStart(e) {
        this.setMouseXY(e);
        this.trigger(chartMouseDown, { target: e.target.id, x: this.mouseX, y: this.mouseY });
        return false;
    }
    /**
     * Handles the accumulation chart resize.
     *
     * @returns {boolean} - Resize method of accumulation chart.
     * @private
     */
    accumulationResize() {
        this.animateSeries = false;
        const args = {
            accumulation: this,
            previousSize: new Size(this.availableSize.width, this.availableSize.height),
            name: resized,
            currentSize: new Size(0, 0),
            chart: this
        };
        const beforeResizeArgs = { name: 'beforeResize', cancelResizedEvent: false };
        if (this.resizeTo) {
            clearTimeout(this.resizeTo);
        }
        this.trigger(beforeResize, beforeResizeArgs);
        if (!beforeResizeArgs.cancelResizedEvent) {
            this.resizeTo = +setTimeout(() => {
                if (this.isDestroyed) {
                    clearTimeout(this.resizeTo);
                    return;
                }
                calculateSize(this);
                args.currentSize = this.availableSize;
                this.trigger(resized, args);
                this.refreshSeries();
                this.refreshChart();
            }, 500);
        }
        return false;
    }
    /**
     * Handles the print method for accumulation chart control.
     *
     * @param {string[] | string | Element} id - The id of the accumulation chart to be printed on the page.
     * @returns {void}
     */
    print(id) {
        // To handle the print funtion in IE and Edge browsers
        const clippath = document.getElementById(this.element.id + '_Series_0').style.clipPath;
        document.getElementById(this.element.id + '_Series_0').style.clipPath = '';
        const exportChart = new PrintUtils(this);
        exportChart.print(id);
        document.getElementById(this.element.id + '_Series_0').style.clipPath = clippath;
    }
    /**
     * Export method for the chart.
     *
     * @param {ExportType} type - The type of export.
     * @param {string} fileName - The name of the file for export.
     * @returns {void}
     */
    export(type, fileName) {
        if (this.exportModule) {
            this.exportModule.export(type, fileName);
            if (this.afterExport) {
                this.exportModule.getDataUrl(this);
            }
        }
    }
    /**
     * Applying styles for accumulation chart element.
     *
     * @param {HTMLElement} element - Specifies the element.
     * @returns {void}
     */
    setStyle(element) {
        element.style.touchAction = 'element';
        element.style.msTouchAction = 'element';
        element.style.msContentZooming = 'none';
        element.style.msUserSelect = 'none';
        element.style.webkitUserSelect = 'none';
        element.style.position = 'relative';
        element.style.display = 'block';
        element.style.overflow = 'hidden';
        element.style.height = (element.style.height || (this.height && this.height.indexOf('%') === -1)) ? element.style.height : 'inherit';
        let tabColor = '';
        switch (this.theme) {
            case 'HighContrastLight':
            case 'HighContrast':
                tabColor = '#969696';
                break;
            case 'MaterialDark':
            case 'FabricDark':
            case 'Bootstrap':
            case 'Bootstrap4':
                tabColor = '#66afe9';
                break;
            case 'Tailwind':
            case 'TailwindDark':
                tabColor = '#4f46e5';
                break;
            case 'Bootstrap5':
            case 'Bootstrap5Dark':
                tabColor = '#0d6efd';
                break;
            case 'Fluent':
            case 'FluentDark':
                tabColor = '#9e9e9e';
                break;
            case 'Fluent2':
            case 'Fluent2Dark':
            case 'Fluent2HighContrast':
                tabColor = '#0078D4';
                break;
            default:
                tabColor = '#9e9e9e';
                break;
        }
    }
    /**
     * Method to set the annotation content dynamically for accumulation.
     *
     * @param {number} annotationIndex - The index of the annotation.
     * @param {string} content - The content to set for the annotation.
     * @returns {void}
     */
    setAnnotationValue(annotationIndex, content) {
        const annotation = this.annotations[annotationIndex];
        let element;
        const parentNode = getElement(this.element.id + '_Annotation_Collections');
        if (content) {
            annotation.content = content;
            if (parentNode) {
                element = this.createElement('div');
                removeElement(this.element.id + '_Annotation_' + annotationIndex);
                this.annotationModule.processAnnotation(annotation, annotationIndex, element);
                parentNode.appendChild(element.children[0]);
            }
            else {
                this.annotationModule.renderAnnotations(getElement(this.element.id + '_Secondary_Element'));
            }
        }
    }
    /**
     * Handles the mouse move on accumulation chart.
     *
     * @param {PointerEvent} e - The pointer event containing mouse coordinates.
     * @returns {boolean} - Mouse move of accumulation chart.
     * @private
     */
    accumulationMouseMove(e) {
        if (!getElement(this.element.id + '_svg')) {
            return false;
        }
        this.setMouseXY(e);
        this.trigger(chartMouseMove, { target: e.target.id, x: this.mouseX, y: this.mouseY });
        if (this.pointMove) {
            this.triggerPointEvent(pointMove, e.target, e);
        }
        if (this.accumulationLegendModule && this.legendSettings.visible) {
            this.accumulationLegendModule.move(e);
        }
        if (this.accumulationDataLabelModule && this.visibleSeries[0] && this.visibleSeries[0].dataLabel.visible) {
            this.accumulationDataLabelModule.move(e, this.mouseX, this.mouseY);
        }
        if (this.centerLabel.hoverTextFormat) {
            this.updateCenterLabel(e);
        }
        if (!this.isTouch) {
            this.titleTooltip(e, this.mouseX, this.mouseY);
        }
        if (this.enableBorderOnMouseMove && this.type === 'Pie' && this.pieSeriesModule &&
            withInBounds(this.mouseX, this.mouseY, this.initialClipRect)) {
            this.pieSeriesModule.findSeries(e, this.series[0].borderRadius);
        }
        this.notify(Browser.touchMoveEvent, e);
        return false;
    }
    titleTooltip(event, x, y, isTouch) {
        const targetId = event.target.id;
        const id = (targetId === (this.element.id + '_title') || targetId === (this.element.id + '_subTitle') ||
            targetId === (this.element.id + '_chart_legend_title'));
        if ((event.target.textContent.indexOf('...') > -1) && id) {
            const title = (targetId === (this.element.id + '_title')) ?
                this.title : (targetId === (this.element.id + '_subTitle')) ? this.subTitle : this.legendSettings.title;
            showTooltip(title, x, y, this.element.offsetWidth, this.element.id + '_EJ2_Title_Tooltip', getElement(this.element.id + '_Secondary_Element'), isTouch);
        }
        else {
            removeElement(this.element.id + '_EJ2_Title_Tooltip');
        }
    }
    /**
     * Handles the keyboard onkeydown on chart.
     *
     * @param {KeyboardEvent} e - The keyboard event.
     * @returns {boolean} - false
     * @private
     */
    accumulationChartKeyDown(e) {
        let actionKey = '';
        if (this.tooltip.enable && ((e.code === 'Tab' && this.previousTargetId.indexOf('Series') > -1) || e.code === 'Escape')) {
            actionKey = 'ESC';
        }
        if (e.code.indexOf('Arrow') > -1) {
            e.preventDefault();
        }
        if (e.ctrlKey && (e.key === 'p')) {
            e.preventDefault();
            actionKey = 'CtrlP';
        }
        if (actionKey !== '') {
            this.chartKeyboardNavigations(e, e.target.id, actionKey);
        }
        if (e.code === 'Tab') {
            this.removeNavigationStyle();
        }
        return false;
    }
    /**
     * Handles the keyboard onkeydown on chart.
     *
     * @param {KeyboardEvent} e - The keyboard event.
     * @returns {boolean} - false
     * @private
     */
    accumulationChartKeyUp(e) {
        let actionKey = '';
        let targetId = e.target['id'];
        const legendElement = getElement(this.element.id + '_chart_legend_translate_g');
        const pagingElement = getElement(this.element.id + '_chart_legend_pageup');
        if (legendElement) {
            const firstChild = legendElement.firstElementChild;
            if (firstChild) {
                let className = firstChild.getAttribute('class');
                if (className && className.indexOf('e-accumulationchart-focused') === -1) {
                    className = className + ' e-accumulationchart-focused';
                }
                else if (!className) {
                    className = 'e-accumulationchart-focused';
                }
                firstChild.setAttribute('class', className);
            }
        }
        if (pagingElement) {
            pagingElement.setAttribute('class', 'e-accumulationchart-focused');
        }
        this.removeNavigationStyle();
        if (e.code === 'Tab') {
            if (this.previousTargetId !== '') {
                if (this.previousTargetId.indexOf('_Point_') > -1 && targetId.indexOf('_Point_') === -1) {
                    const groupElement = document.getElementById(this.previousTargetId).parentElement;
                    this.setTabIndex(groupElement.children[this.currentPointIndex], groupElement.firstElementChild);
                    this.currentPointIndex = 0;
                }
                else if (this.previousTargetId.indexOf('_chart_legend_page') > -1 && targetId.indexOf('_chart_legend_page') === -1 &&
                    targetId.indexOf('_chart_legend_g_') === -1) {
                    this.setTabIndex(e.target, pagingElement);
                }
                else if (this.previousTargetId.indexOf('_chart_legend_g_') > -1 && targetId.indexOf('chart_legend_g_') === -1 && legendElement) {
                    this.setTabIndex(legendElement.children[this.currentLegendIndex], legendElement.firstElementChild);
                }
            }
            this.previousTargetId = targetId;
            if (targetId.indexOf('_chart_legend_g_') > -1 && this.highlightMode !== 'None') {
                targetId = e.target['lastElementChild'].id;
                actionKey = 'Tab';
            }
            else if (targetId.indexOf('_Point_') > -1 && (this.highlightMode !== 'None' || this.tooltip.enable)) {
                actionKey = 'Tab';
            }
        }
        else if (e.code.indexOf('Arrow') > -1) {
            e.preventDefault();
            if (targetId.indexOf('_chart_legend_page') > -1) {
                e.target.removeAttribute('tabindex');
                this.previousTargetId = targetId = this.element.id + '_chart_legend_page' + (e.code === 'ArrowRight' ? 'up' : 'down');
                this.focusTarget(getElement(targetId));
            }
            else if ((targetId.indexOf('_chart_legend_') > -1) && legendElement) {
                e.target.removeAttribute('tabindex');
                this.currentLegendIndex += (e.code === 'ArrowUp' || e.code === 'ArrowRight') ? +1 : -1;
                this.currentLegendIndex = this.getActualIndex(this.currentLegendIndex, (targetId.indexOf('_chart_legend_template') > -1) ? this.accumulationLegendModule.legendCollections.length : legendElement.children.length);
                const currentLegend = (targetId.indexOf('_chart_legend_template') > -1) ?
                    getElement(this.element.id + '_chart_legend_template_0' + this.currentLegendIndex) :
                    legendElement.children[this.currentLegendIndex];
                this.focusTarget(currentLegend);
                this.removeNavigationStyle();
                this.setNavigationStyle((targetId.indexOf('_chart_legend_template') > -1) ? (this.element.id + '_chart_legend_template_0' + this.currentLegendIndex) : currentLegend.id);
                this.previousTargetId = targetId = (targetId.indexOf('_chart_legend_template') > -1) ? currentLegend.id : currentLegend.lastElementChild.id;
                actionKey = this.highlightMode !== 'None' ? 'ArrowMove' : '';
            }
            else if (targetId.indexOf('_Point_') > -1) {
                e.target.setAttribute('tabindex', '-1');
                this.currentPointIndex += (e.code === 'ArrowUp' || e.code === 'ArrowRight') ? +1 : -1;
                let totalLength = 0;
                for (let i = 0; i < e.target['parentElement'].children.length; i++) {
                    totalLength = e.target['parentElement'].children[i].id.indexOf('_Point_') > -1 ? totalLength + 1 : totalLength;
                }
                this.currentPointIndex = this.getActualIndex(this.currentPointIndex, totalLength);
                targetId = this.element.id + '_Series_0_Point_' + this.currentPointIndex;
                this.focusTarget(getElement(targetId));
                this.removeNavigationStyle();
                this.setNavigationStyle(targetId);
                actionKey = this.tooltip.enable ? 'ArrowMove' : '';
            }
        }
        else if ((e.code === 'Enter' || e.code === 'Space') && ((targetId.indexOf('_chart_legend_') > -1) ||
            (targetId.indexOf('_Point_') > -1))) {
            targetId = (targetId.indexOf('_chart_legend_g') > -1) ? e.target['lastElementChild'].id : targetId;
            actionKey = 'Enter';
        }
        if (actionKey !== '') {
            this.chartKeyboardNavigations(e, targetId, actionKey);
        }
        if (e.code === 'Tab') {
            this.setNavigationStyle(targetId);
        }
        return false;
    }
    setTabIndex(previousElement, currentElement) {
        if (previousElement) {
            previousElement.removeAttribute('tabindex');
        }
        if (currentElement) {
            currentElement.setAttribute('tabindex', '0');
        }
    }
    getActualIndex(index, totalLength) {
        return index > totalLength - 1 ? 0 : (index < 0 ? totalLength - 1 : index);
    }
    focusTarget(element) {
        let className = element.getAttribute('class');
        element.setAttribute('tabindex', '0');
        if (className && className.indexOf('e-accumulationchart-focused') === -1) {
            className = className + ' e-accumulationchart-focused';
        }
        else if (!className) {
            className = 'e-accumulationchart-focused';
        }
        element.setAttribute('tabindex', '0');
        element.setAttribute('class', className);
        element.focus();
        return element.id;
    }
    /**
     * Handles the document onkey.
     *
     * @param {KeyboardEvent} e - The keyboard event.
     * @returns {void}
     * @private
     */
    documentKeyHandler(e) {
        // 74 - J
        if (e.altKey && e.keyCode === 74 && !isNullOrUndefined(this.element)) {
            this.element.focus();
        }
    }
    /**
     * Handles to set style for key event on the document.
     *
     * @param {target} target - element which currently focused.
     * @returns {void}
     * @private
     */
    setNavigationStyle(target) {
        const currentElement = document.getElementById(target);
        if (currentElement) {
            currentElement.style.setProperty('outline', `${this.focusBorderWidth}px solid ${this.focusBorderColor || this.themeStyle.tabColor}`);
            currentElement.style.setProperty('margin', `${this.focusBorderMargin}px`);
        }
    }
    /**
     * Handles to remove style for key event on the document.
     *
     * @returns {void}
     * @private
     */
    removeNavigationStyle() {
        const currentElement = document.querySelectorAll(`path[id*=_Series_0_Point_], [id*=${this.element.id}], [id*=_ChartBorder], text[id*=_title],g[id*=_chart_legend]`);
        if (currentElement) {
            currentElement.forEach((element) => {
                if (element instanceof HTMLElement || element instanceof SVGElement) {
                    element.style.setProperty('outline', 'none');
                    element.style.setProperty('margin', '');
                }
            });
        }
    }
    chartKeyboardNavigations(e, targetId, actionKey) {
        this.isLegendClicked = false;
        switch (actionKey) {
            case 'Tab':
            case 'ArrowMove':
                if (this.accumulationHighlightModule) {
                    //  this.accumulationHighlightModule.removeHighlightElements();
                }
                if (targetId.indexOf('_Point_') > -1) {
                    const seriesIndex = +(targetId.split('_Series_')[1].split('_Point_')[0]);
                    const pointIndex = +(targetId.split('_Series_')[1].replace('_Symbol', '').split('_Point_')[1]);
                    const pointRegion = this.visibleSeries[seriesIndex].points[pointIndex].symbolLocation;
                    this.mouseX = pointRegion.x + this.initialClipRect.x;
                    this.mouseY = pointRegion.y + this.initialClipRect.y;
                    if (this.accumulationHighlightModule) {
                        let targetElement = getElement(targetId);
                        if (!isNullOrUndefined(targetElement)) {
                            if (targetElement.id.indexOf('text') > 1) {
                                targetElement = getElement(targetElement.id.replace('text', 'shape'));
                            }
                            if ((targetElement).hasAttribute('class') && (targetElement).getAttribute('class').indexOf('highlight') > -1) {
                                return;
                            }
                            this.accumulationHighlightModule.calculateSelectedElements(this, targetElement, 'mousemove');
                            return;
                        }
                    }
                    if (this.accumulationTooltipModule) {
                        const series = this.visibleSeries[seriesIndex];
                        let data;
                        if (series.enableTooltip) {
                            data = new AccPointData(series.points[pointIndex], series);
                        }
                        this.accumulationTooltipModule.renderSeriesTooltip(this, data);
                    }
                }
                if (this.accumulationHighlightModule && this.highlightMode !== 'None') {
                    targetId = targetId.indexOf('_chart_legend_g_') > -1 ? document.getElementById(targetId).firstChild['id'] : targetId;
                    const legendID = this.element.id + '_chart_legend';
                    const legendItemsId = [legendID + '_text_', legendID + '_shape_marker_',
                        legendID + '_shape_'];
                    for (let i = 0; i < legendItemsId.length; i++) {
                        const id = legendItemsId[i];
                        if (targetId.indexOf(id) > -1) {
                            document.getElementById(targetId).setAttribute('class', '');
                            this.accumulationHighlightModule.legendSelection(this, 0, parseInt(targetId.split(id)[1], 10), getElement(targetId), 'mousemove');
                            break;
                        }
                    }
                }
                break;
            case 'Enter':
            case 'Space':
                if (targetId.indexOf('_chart_legend_') > -1 && this.accumulationLegendModule) {
                    this.isLegendClicked = true;
                    this.accumulationLegendModule.click(e);
                    this.focusChild((targetId.indexOf('_chart_legend_template_') > -1) ? document.getElementById(targetId) : document.getElementById(targetId).parentElement);
                    this.setNavigationStyle((targetId.indexOf('_chart_legend_template_') > -1) ? document.getElementById(targetId).id : document.getElementById(targetId).parentElement.id);
                }
                else {
                    if (this.accumulationSelectionModule) {
                        this.accumulationSelectionModule.calculateSelectedElements(this, document.getElementById(targetId), 'click');
                    }
                    this.setNavigationStyle(targetId);
                }
                break;
            case 'CtrlP':
                this.print();
                break;
            case 'ESC':
                if (this.accumulationTooltipModule) {
                    this.accumulationTooltipModule.removeTooltip(1);
                }
                break;
        }
    }
    focusChild(element) {
        element.setAttribute('tabindex', '0');
        let className = element.getAttribute('class');
        element.setAttribute('tabindex', '0');
        if (className && className.indexOf('e-accumulationchart-focused') === -1) {
            className = 'e-accumulationchart-focused ' + className;
        }
        else if (!className) {
            className = 'e-accumulationchart-focused';
        }
        element.setAttribute('class', className);
        element.focus();
        return element.id;
    }
    /**
     * Handles the mouse double click on accumulation chart.
     *
     * @param {PointerEvent} e - The pointer event.
     * @returns {boolean} - Mouse double click of accumulation chart.
     * @private
     */
    accumulationOnDoubleClick(e) {
        this.trigger(chartDoubleClick, { target: e.target.id, x: this.mouseX, y: this.mouseY });
        return false;
    }
    /**
     * Handles the mouse click on accumulation chart.
     *
     * @param {PointerEvent} e - The pointer event.
     * @returns {boolean} - Mouse click of accumulation chart.
     * @private
     */
    accumulationOnMouseClick(e) {
        this.setMouseXY(e);
        if (this.accumulationLegendModule && this.legendSettings.visible) {
            this.accumulationLegendModule.click(e);
        }
        if (this.selectionMode !== 'None' && this.accumulationSelectionModule) {
            this.accumulationSelectionModule.calculateSelectedElements(this, e.target, e.type);
        }
        if (this.visibleSeries[0].explode) {
            this.accBaseModule.processExplode(e);
        }
        if (this.enableBorderOnMouseMove && this.pieSeriesModule && this.type === 'Pie') {
            this.pieSeriesModule.findSeries(e, this.series[0].borderRadius);
        }
        this.trigger(chartMouseClick, { target: e.target.id, x: this.mouseX, y: this.mouseY });
        if (this.pointClick) {
            this.triggerPointEvent(pointClick, e.target, e);
        }
        this.removeNavigationStyle();
        return false;
    }
    triggerPointEvent(event, element, e) {
        const evt = e;
        const indexes = indexFinder(element.id, true);
        if (indexes.series >= 0 && indexes.point >= 0) {
            this.trigger(event, {
                series: this.series[indexes.series],
                point: this.series[indexes.series].points[indexes.point],
                seriesIndex: indexes.series, pointIndex: indexes.point,
                x: this.mouseX, y: this.mouseY, pageX: evt.pageX, pageY: evt.pageY
            });
        }
    }
    /**
     * Handles the mouse right click on accumulation chart.
     *
     * @param {MouseEvent | PointerEvent} event - The mouse event or pointer event.
     * @returns {boolean} - Right click of accumulation chart.
     * @private
     */
    accumulationRightClick(event) {
        if (event.buttons === 2 && event.pointerType === 'touch') {
            event.preventDefault();
            event.stopPropagation();
            return false;
        }
        return true;
    }
    /**
     * Handles the mouse leave on accumulation chart.
     *
     * @param {PointerEvent} e - The pointer event.
     * @returns {boolean} - Mouse leave of accumulation chart.
     * @private
     */
    accumulationMouseLeave(e) {
        this.setMouseXY(e);
        this.trigger(chartMouseLeave, { target: e.target.id, x: this.mouseX, y: this.mouseY });
        this.notify(Browser.isPointer ? 'pointerleave' : 'mouseleave', e);
        let borderElement = document.getElementById(this.element.id + 'PointHover_Border');
        if (borderElement) {
            this.pieSeriesModule.removeBorder(borderElement, 1000);
            borderElement = null;
        }
        return false;
    }
    /**
     * Method to set culture for chart.
     *
     * @returns {void}
     */
    setCulture() {
        this.intl = new Internationalization();
    }
    /**
     * Method to create SVG element for accumulation chart.
     *
     * @returns {void}
     */
    createPieSvg() {
        this.removeSvg();
        createSvg(this);
    }
    /**
     * To Remove the SVG from accumulation chart.
     *
     * @returns {boolean} - Remove svg.
     * @private
     */
    removeSvg() {
        if (this.redraw) {
            return null;
        }
        removeElement(this.element.id + '_Secondary_Element');
        if (this.svgObject) {
            while (this.svgObject.childNodes.length > 0) {
                this.svgObject.removeChild(this.svgObject.firstChild);
            }
            if (!this.svgObject.hasChildNodes() && this.svgObject.parentNode) {
                remove(this.svgObject);
            }
        }
        removeElement('EJ2_legend_tooltip');
        removeElement('EJ2_datalabel_tooltip');
        removeElement(this.element.id + 'PointHover_Border');
    }
    /**
     * Method to create the secondary element for tooltip, datalabel and annotaitons.
     *
     * @returns {void}
     */
    createSecondaryElement() {
        const element = redrawElement(this.redraw, this.element.id + '_Secondary_Element') ||
            this.createElement('div', {
                id: this.element.id + '_Secondary_Element',
                styles: 'position: relative'
            });
        appendChildElement(false, this.element, element, this.redraw);
    }
    /**
     * Method to find visible series based on series types.
     *
     * @returns {void}
     */
    calculateVisibleSeries() {
        this.visibleSeries = [];
        for (let i = 0, length = this.series.length; i < length; i++) {
            this.series[i].index = i;
            if (this.series[i].type === this.type && this.visibleSeries.length === 0) {
                this.visibleSeries.push(this.series[i]);
                break;
            }
        }
    }
    /**
     * To find points from dataSource.
     *
     * @param {boolean} render - Indicates whether to render the points (default: true).
     * @returns {void}
     */
    processData(render = true) {
        this.seriesCounts = 0;
        for (const series of this.visibleSeries) {
            series.dataModule = new Data(series.dataSource || this.dataSource, series.query);
            series.refreshDataManager(this, render);
        }
    }
    /**
     * To refresh the accumulation chart.
     *
     * @private
     * @returns {void}
     */
    refreshChart() {
        this.doGrouppingProcess();
        this.createPieSvg();
        this.calculateBounds();
        this.renderElements();
        removeElement('chartmeasuretext');
    }
    /**
     * Method to find groupped points.
     *
     * @returns {void}
     */
    doGrouppingProcess() {
        const series = this.visibleSeries[0];
        if (!isNullOrUndefined(series.resultData) && ((!isNullOrUndefined(series.lastGroupTo) &&
            series.lastGroupTo !== series.groupTo))) {
            series.getPoints(series.resultData, this);
        }
    }
    /**
     * Method to calculate bounds for accumulation chart.
     *
     * @returns {void}
     */
    calculateBounds() {
        this.initialClipRect = new Rect(this.margin.left, this.margin.top, this.availableSize.width, this.availableSize.height);
        this.titleCollection = [];
        this.subTitleCollection = [];
        let titleHeight = 0;
        let subTitleHeight = 0;
        let maxWidth = 0;
        let titleWidth = 0;
        if (this.title) {
            this.titleCollection = getTitle(this.title, this.titleStyle, this.initialClipRect.width, this.enableRtl, this.themeStyle.chartTitleFont);
        }
        titleHeight = this.title ? measureText(this.title, this.titleStyle, this.themeStyle.chartTitleFont).height * this.titleCollection.length : titleHeight;
        if (this.subTitle) {
            for (const titleText of this.titleCollection) {
                titleWidth = measureText(titleText, this.titleStyle, this.themeStyle.chartSubTitleFont).width;
                maxWidth = titleWidth > maxWidth ? titleWidth : maxWidth;
            }
            this.subTitleCollection = getTitle(this.subTitle, this.subTitleStyle, this.initialClipRect.width, this.enableRtl, this.themeStyle.chartSubTitleFont);
            subTitleHeight = (measureText(this.subTitle, this.subTitleStyle, this.themeStyle.chartSubTitleFont).height * this.subTitleCollection.length);
        }
        let left = this.margin.left + this.border.width;
        let width = this.availableSize.width - left - this.margin.right - this.border.width;
        let top = this.margin.top + this.border.width;
        let height = this.availableSize.height - top - this.border.width - this.margin.bottom;
        const marginTotal = subTitleHeight + titleHeight;
        switch (this.titleStyle.position) {
            case 'Top':
                left = 0;
                top = subTitleHeight + titleHeight;
                width = this.margin.right + this.margin.left;
                height = this.margin.bottom + this.margin.top;
                break;
            case 'Bottom':
                height -= (marginTotal + this.margin.bottom * 2);
                break;
            case 'Left':
                left += marginTotal;
                width -= marginTotal;
                break;
            case 'Right':
                width -= marginTotal;
                break;
        }
        if (this.titleStyle.position !== 'Top') {
            this.initialClipRect = new Rect(left, top, width, height);
        }
        else {
            this.initialClipRect = subtractRect(this.initialClipRect, new Rect(left, top, width, height));
        }
        this.calculateLegendBounds();
    }
    /**
     * Method to calculate legend bounds for accumulation chart.
     *
     * @returns {void}
     */
    calculateLegendBounds() {
        if (!this.accumulationLegendModule || !this.legendSettings.visible) {
            return null;
        }
        this.accumulationLegendModule.getLegendOptions(this, this.visibleSeries);
        this.accumulationLegendModule.calculateLegendBounds(this.initialClipRect, this.availableSize, null);
    }
    /**
     * To render elements for accumulation chart.
     *
     * @private
     * @returns {void}
     */
    renderElements() {
        this.applyGradientsToSeries();
        this.renderBorder();
        this.createSecondaryElement();
        this.renderSeries();
        this.renderTitle();
        this.renderCenterLabel(true);
        this.renderLegend();
        appendChildElement(false, this.element, this.svgObject, this.redraw);
        this.processSelection();
        this.processExplode();
        this.renderAnnotation();
        this.setSecondaryElementPosition();
        const isAllSeriesEmpty = this.series.every((s) => {
            const dataSource = s.dataSource || [];
            return dataSource.length === 0;
        });
        if (isAllSeriesEmpty && !isNullOrUndefined(this.noDataTemplate)) {
            this.renderNoDataTemplate(true);
        }
        this.trigger('loaded', { accumulation: this, chart: this });
        this.animateSeries = false;
    }
    /**
     * To set the left and top position for data label template for center aligned chart.
     *
     * @private
     * @returns {void}
     */
    setSecondaryElementPosition() {
        const tooltipParent = getElement(this.element.id + '_Secondary_Element');
        if (!tooltipParent) {
            return;
        }
        const rect = this.element.getBoundingClientRect();
        const svgRect = getElement(this.element.id + '_svg').getBoundingClientRect();
        tooltipParent.style.left = Math.max(svgRect.left - rect.left, 0) + 'px';
        tooltipParent.style.top = Math.max(svgRect.top - rect.top, 0) + 'px';
    }
    renderNoDataTemplate(allowTemplate) {
        const existingTemplate = this.element.querySelector(`#${this.element.id}_NoDataTemplate_wrapper`);
        if (existingTemplate) {
            existingTemplate.remove();
        }
        if (allowTemplate) {
            const rawTemplate = typeof this.noDataTemplate === 'function' ? this.noDataTemplate() : this.noDataTemplate;
            const sanitizedTemplate = this.enableHtmlSanitizer && typeof rawTemplate === 'string'
                ? this.sanitize(rawTemplate) : rawTemplate;
            const wrapper = createElement('div', {
                id: this.element.id + '_NoDataTemplate_wrapper'
            });
            let borderStrokeWidth = 0;
            const borderElement = this.element.querySelector(`#${this.element.id}_border`);
            if (borderElement) {
                borderStrokeWidth = parseFloat(borderElement.getAttribute('stroke-width'));
            }
            let chartAreaWidth = this.availableSize.width - borderStrokeWidth * 2;
            let chartAreaHeight = this.availableSize.height - borderStrokeWidth * 2;
            let topOffset = borderStrokeWidth;
            let leftOffset = borderStrokeWidth;
            let titleOffset = 0;
            if (this.title) {
                const titleSize = measureText(this.title, this.titleStyle, this.themeStyle.chartTitleFont).height;
                const subTitleSize = this.subTitle
                    ? measureText(this.subTitle, this.subTitleStyle, this.themeStyle.chartSubTitleFont).height : 0;
                const spacing = this.subTitle ? 5 : 0;
                titleOffset = titleSize + subTitleSize + spacing;
                switch (this.titleStyle.position) {
                    case 'Top':
                        topOffset += titleOffset + 10;
                        chartAreaHeight -= (titleOffset + 10);
                        break;
                    case 'Left':
                        leftOffset += titleOffset + 10;
                        chartAreaWidth -= (titleOffset + 10);
                        break;
                    case 'Bottom':
                        chartAreaHeight -= (titleOffset + 16);
                        break;
                    case 'Right':
                        chartAreaWidth -= (titleOffset + 10);
                        break;
                }
            }
            wrapper.style.cssText = `position: absolute; top: ${topOffset}px; left: ${leftOffset}px; width: ${chartAreaWidth}px;
                height: ${chartAreaHeight}px; z-index: 1;
            `;
            const element = getElement(this.element.id + '_Secondary_Element');
            element.appendChild(wrapper);
            createTemplate(wrapper, null, sanitizedTemplate, this, null, null, this.element.id + '_NoData');
        }
    }
    /**
     * To render the annotaitions for accumulation series.
     *
     * @private
     * @returns {void}
     */
    renderAnnotation() {
        if (this.annotationModule) {
            this.annotationModule.renderAnnotations(getElement(this.element.id + '_Secondary_Element'));
        }
    }
    /**
     * Method to process the explode in accumulation chart.
     *
     * @private
     * @returns {void}
     */
    processExplode() {
        if (this.redraw) {
            return null;
        }
        if (!this.visibleSeries[0].explode) {
            return null;
        }
        this.accBaseModule.invokeExplode();
    }
    /**
     * Method to render series for accumulation chart.
     *
     * @returns {void}
     */
    renderSeries() {
        if (!this.redraw) {
            this.svgObject.appendChild(this.renderer.createGroup({ id: this.element.id + '_SeriesCollection' }));
        }
        for (const series of this.visibleSeries) {
            if (series.visible && this[(firstToLowerCase(series.type) + 'SeriesModule')]) {
                this[(firstToLowerCase(series.type) + 'SeriesModule')].initProperties(this, series);
                series.renderSeries(this, this.redraw);
            }
        }
    }
    /**
     * Method to render border for accumulation chart.
     *
     * @returns {void}
     */
    renderBorder() {
        const padding = this.border.width;
        const rect = new RectOption(this.element.id + '_border', this.background || this.themeStyle.background, this.border, 1, new Rect(padding / 2, padding / 2, this.availableSize.width - padding, this.availableSize.height - padding), 0, 0, '', this.border.dashArray);
        const htmlObject = this.renderer.drawRectangle(rect);
        htmlObject.setAttribute('aria-hidden', 'true');
        appendChildElement(false, this.svgObject, htmlObject, this.redraw);
        // to draw back ground image for accumulation chart
        const backGroundImage = this.backgroundImage;
        if (backGroundImage) {
            const image = new ImageOption(this.availableSize.height - padding, this.availableSize.width - padding, backGroundImage, 0, 0, this.element.id + '_background', 'visible', 'none');
            appendChildElement(false, this.svgObject, this.renderer.drawImage(image), this.redraw);
        }
    }
    /**
     * Method to render legend for accumulation chart.
     *
     * @returns {void}
     */
    renderLegend() {
        if (!this.accumulationLegendModule || !this.legendSettings.visible) {
            return null;
        }
        if (this.accumulationLegendModule.legendCollections.length) {
            if (this.visibleSeries[0].type === 'Pie') {
                this.accumulationLegendModule.getSmartLegendLocation(this.visibleSeries[0].labelBound, this.accumulationLegendModule.legendBounds, this.margin);
            }
            this.accumulationLegendModule.renderLegend(this, this.legendSettings, this.accumulationLegendModule.legendBounds, this.redraw);
        }
    }
    /**
     * To process the selection in accumulation chart.
     *
     * @private
     * @returns {void}
     */
    processSelection() {
        let selectedDataIndexes = [];
        if (this.accumulationSelectionModule && this.selectionMode !== 'None') {
            selectedDataIndexes = extend([], this.accumulationSelectionModule.selectedDataIndexes, null, true);
            this.accumulationSelectionModule.invokeSelection(this);
        }
        if (this.accumulationHighlightModule && this.highlightMode !== 'None') {
            this.accumulationHighlightModule.invokeHighlight(this);
        }
        if (selectedDataIndexes.length > 0) {
            this.accumulationSelectionModule.selectedDataIndexes = selectedDataIndexes;
            this.accumulationSelectionModule.redrawSelection(this);
        }
    }
    /**
     * To render title for accumulation chart.
     *
     * @returns {void}
     */
    renderTitle() {
        const margin = this.margin;
        if (!this.title) {
            return null;
        }
        let textAnchor = getTextAnchor(this.titleStyle.textAlignment, this.enableRtl);
        const titleSize = measureText(this.title, this.titleStyle, this.themeStyle.chartTitleFont);
        const padding = 20;
        const titleHeight = this.margin.top + (titleSize.height * 3 / 4);
        const legendHeight = this.accumulationLegendModule === undefined ? 0 : this.legendSettings.position === 'Top' ?
            this.accumulationLegendModule.legendBounds.height : 0;
        const explode = this.explodeDistance === 0 ? 0 : this.explodeDistance;
        const expodeValue = legendHeight !== 0 ? 0 : explode / 2;
        const rect = new Rect(margin.left, 0, this.availableSize.width - margin.left - margin.right, 0);
        let positionY = this.margin.top + ((titleSize.height) * 3 / 4);
        let positionX = titlePositionX(rect, this.titleStyle || this.themeStyle.chartTitleFont);
        let rotation;
        const alignment = this.titleStyle.textAlignment;
        const subtitleSize = measureText(this.subTitle, this.subTitleStyle, this.themeStyle.chartSubTitleFont);
        switch (this.titleStyle.position) {
            case 'Top':
                positionX = titlePositionX(rect, this.titleStyle);
                positionY = titleHeight;
                break;
            case 'Bottom':
                positionX += textAnchor === 'start' ? this.border.width :
                    textAnchor === 'end' ? this.border.width : 0;
                positionY = this.availableSize.height - this.margin.bottom - subtitleSize.height - (titleSize.height / 2);
                break;
            case 'Left':
                positionX = this.margin.left + ((titleSize.height) * 3 / 4);
                positionY = alignment === 'Near' ? margin.bottom + this.border.width :
                    alignment === 'Far' ? this.availableSize.height - margin.bottom - this.border.width : this.availableSize.height / 2;
                textAnchor = alignment === 'Near' ? 'end' : alignment === 'Far' ? 'start' : 'middle';
                textAnchor = this.enableRtl ? (textAnchor === 'end' ? 'start' : textAnchor === 'start' ? 'end' : textAnchor) : textAnchor;
                rotation = 'rotate(' + -90 + ',' + positionX + ',' + positionY + ')';
                break;
            case 'Right':
                positionX = this.availableSize.width - this.margin.right - ((titleSize.height) * 3 / 4);
                positionY = alignment === 'Near' ? margin.bottom + this.border.width :
                    alignment === 'Far' ? this.availableSize.height - margin.bottom - this.border.width : this.availableSize.height / 2;
                textAnchor = alignment === 'Near' ? 'start' : alignment === 'Far' ? 'end' : 'middle';
                textAnchor = this.enableRtl ? (textAnchor === 'end' ? 'start' : textAnchor === 'start' ? 'end' : textAnchor) : textAnchor;
                rotation = 'rotate(' + 90 + ',' + positionX + ',' + positionY + ')';
                break;
            case 'Custom':
                positionX = this.titleStyle.x;
                positionY = this.titleStyle.y;
                textAnchor = 'middle';
                break;
        }
        const options = new TextOption(this.element.id + '_title', positionX, positionY, textAnchor, this.titleCollection, rotation, 'auto');
        const space = (this.series[0].type === 'Pie' && this.visibleSeries[0].dataLabel.position === 'Outside' && this.visibleSeries[0].dataLabel.connectorStyle.length) ? stringToNumber(this.visibleSeries[0].dataLabel.connectorStyle.length, this.accBaseModule.radius) : 0;
        if (!this.subTitle && (this.series[0].type !== 'Funnel' && this.series[0].type !== 'Pyramid') && this.titleStyle.position === 'Top') {
            options.y = parseInt(this.series[0].radius, 10) >= 80 ? options.y :
                (this.accBaseModule.center.y - this.accBaseModule.radius - padding
                    - titleHeight - legendHeight - expodeValue - space);
            if (this.series[0].type === 'Pie' && (parseInt(this.series[0].radius, 10) < 80 || isNaN(parseInt(this.series[0].radius, 10)))) {
                options.y = options.y < (this.initialClipRect.y - legendHeight) ? (this.initialClipRect.y - legendHeight) : options.y;
            }
        }
        const element = textElement(this.renderer, options, this.titleStyle, this.titleStyle.color || this.themeStyle.chartTitleFont.color, this.svgObject, false, this.redraw, null, null, null, null, null, null, null, null, this.themeStyle.chartTitleFont);
        if (element) {
            element.setAttribute('tabindex', '0');
            element.style.outline = 'none';
            element.parentNode.insertBefore(element, this.svgObject.children && this.svgObject.children[1]);
        }
        if (this.subTitle) {
            this.renderSubTitle(options);
        }
    }
    /**
     * To update center label on mouse move.
     *
     * @param {Event} event - The mouse move event.
     * @returns {void}
     */
    updateCenterLabel(event) {
        const data = this.getPieData(event);
        this.format = (data.point == null) ? '' : this.parseFormat(data.point, this.visibleSeries[0], this.centerLabel.hoverTextFormat);
        this.renderCenterLabel();
    }
    /**
     * Function to get pie data on mouse move.
     *
     * @param {PointerEvent | TouchEvent} e - The event object containing mouse or touch coordinates.
     * @returns {AccPointData} - The data of the pie.
     */
    getPieData(e) {
        const dataIndex = indexFinder(e.target.id, true);
        if (!isNaN(dataIndex.series)) {
            return new AccPointData(this.visibleSeries[0].points[dataIndex.point], this.visibleSeries[0]);
        }
        return new AccPointData(null, null);
    }
    /**
     * Function to get format of pie data on mouse move.
     *
     * @param {AccPoints} point - The point data.
     * @param {AccumulationSeries} series - The series to which the point belongs.
     * @param {string} format - The format string for the data.
     * @returns {string} - The formatted data.
     */
    parseFormat(point, series, format) {
        let value;
        let textValue;
        const regExp = RegExp;
        for (const dataValue of Object.keys(point)) {
            value = new regExp('${point' + '.' + dataValue + '}', 'gm');
            format = format.replace(value.source, point[dataValue]);
        }
        for (const dataValue of Object.keys(Object.getPrototypeOf(series))) {
            value = new regExp('${series' + '.' + dataValue + '}', 'gm');
            textValue = series[dataValue];
            format = format.replace(value.source, textValue);
        }
        return format;
    }
    /**
     * To render center label for accumulation chart.
     *
     * @param {boolean} isanimate - Specifies whether to animate the rendering.
     * @param {boolean} pointAnimation - Specifies whether point animation is enabled.
     * @private
     * @returns {void}
     */
    renderCenterLabel(isanimate, pointAnimation) {
        if (!this.centerLabel.text || this.type !== 'Pie') {
            return null;
        }
        const initialPositions = [];
        if (pointAnimation) {
            const tspanElements = (getElement(this.element.id + '_centerLabel')).querySelectorAll('tspan');
            tspanElements.forEach((tspan) => {
                initialPositions.push({
                    x: tspan.getAttribute('x'),
                    y: tspan.getAttribute('y')
                });
            });
        }
        const series = this.series[0];
        let ypos;
        const getAnchor = getTextAnchor(this.centerLabel.textStyle.textAlignment, this.enableRtl);
        const padding = 10;
        // To get side of square inside the circle , which is considered as maxwidth , d*sqrt(0.5)
        const maxwidth = this.pieSeriesModule.innerRadius ? ((2 * this.pieSeriesModule.innerRadius) * (0.7071067)) :
            ((2 * this.pieSeriesModule.radius) * (0.7071067));
        const labelCollection = (this.format || this.centerLabel.text).split('<br>');
        const centerLabelSize = measureText(labelCollection[0], this.centerLabel.textStyle, this.themeStyle.chartTitleFont);
        const collectionLength = labelCollection.length;
        for (let i = 0; i < collectionLength; i++) {
            const labelSize = measureText(labelCollection[i], this.centerLabel.textStyle, this.themeStyle.chartTitleFont);
            if (labelSize.width > maxwidth) {
                labelCollection.splice(i, 1, ...(textWrap(labelCollection[i], maxwidth, this.centerLabel.textStyle, this.enableRtl, null, null, this.themeStyle.chartTitleFont)));
            }
        }
        if (centerLabelSize.height * (labelCollection.length) > maxwidth) {
            ypos = this.accBaseModule.center.y + ((centerLabelSize.height + padding) / 2) - (maxwidth / 2);
        }
        else if ((series.startAngle && series.endAngle) && (Math.abs(series.endAngle - series.startAngle) === 180)) {
            ypos = this.accBaseModule.center.y - (centerLabelSize.height * labelCollection.length / 2) +
                ((centerLabelSize.height + padding) / 2) - this.pieSeriesModule.innerRadius / 2 +
                (this.pieSeriesModule.innerRadius ? padding : 0);
            if ((centerLabelSize.height * (labelCollection.length) + this.pieSeriesModule.innerRadius / 2 + padding > maxwidth)) {
                ypos = this.accBaseModule.center.y + ((centerLabelSize.height + padding) / 2) - (maxwidth / 2);
            }
        }
        else {
            ypos = labelCollection.length > 1 ? (this.accBaseModule.center.y - (centerLabelSize.height * labelCollection.length / 2) +
                ((centerLabelSize.height + padding) / 2)) : (this.accBaseModule.center.y + (centerLabelSize.height) / 4);
        }
        const options = new TextOption(this.element.id + '_centerLabel', (((this.series[0].animation.enable && animationMode !== 'Disable') || animationMode === 'Enable') && isanimate) ? this.pieSeriesModule.center.x - 1 : this.pieSeriesModule.center.x, ypos, getAnchor, '', '', 'auto');
        const element = textElement(this.renderer, options, this.centerLabel.textStyle, this.centerLabel.textStyle.color ||
            this.themeStyle.chartTitleFont.color, this.svgObject, false, this.redraw, null, null, null, null, null, null, null, null, this.themeStyle.chartTitleFont);
        const tspanElements = [];
        for (let i = 0; i < labelCollection.length; i++) {
            const tspanOption = { x: options.x, y: options.y + (i * centerLabelSize.height), fill: '' };
            const tspanElement = this.renderer.createTSpan(tspanOption, '');
            tspanElement.style.fontFamily = 'inherit';
            tspanElement.style.fontStyle = 'inherit';
            tspanElement.style.fontSize = 'inherit';
            tspanElement.style.fontWeight = (labelCollection[i].indexOf('<b>') > -1 || labelCollection[i].indexOf('</b>') > -1) ? 'bold' : 'inherit';
            tspanElement.textContent = labelCollection[i].replace(/<\/?b>/g, '');
            element.appendChild(tspanElement);
            tspanElements.push(tspanElement);
        }
        if (pointAnimation) {
            tspanElements.forEach((tspanElement, index) => {
                this.animateTspan(tspanElement, Number(initialPositions[index].x), Number(initialPositions[index].y), Number(tspanElement.getAttribute('x')), Number(tspanElement.getAttribute('y')), this.duration);
            });
        }
        if (isanimate && ((this.series[0].animation.enable && animationMode !== 'Disable') || animationMode === 'Enable') && this.animateSeries) {
            this.centerLabelDelay(element);
        }
    }
    /**
     * Animates the x and y attributes of a tspan element.
     *
     * @param {HTMLElement} element - The tspan element to animate.
     * @param {number} startx - The initial x coordinate of the tspan.
     * @param {number} starty - The initial y coordinate of the tspan.
     * @param {number} endx - The final x coordinate of the tspan.
     * @param {number} endy - The final y coordinate of the tspan.
     * @param {number} duration - The duration of the animation in milliseconds.
     *
     * @private
     * @returns {void}
     */
    animateTspan(element, startx, starty, endx, endy, duration) {
        new Animation({}).animate(element, {
            duration: duration,
            progress: (args) => {
                element.style.animation = '';
                const progress = args.timeStamp / args.duration;
                const currentX = startx + (endx - startx) * progress;
                const currentY = starty + (endy - starty) * progress;
                element.setAttribute('x', currentX.toString());
                element.setAttribute('y', currentY.toString());
            },
            end: () => {
                element.setAttribute('x', endx.toString());
                element.setAttribute('y', endy.toString());
            }
        });
    }
    /**
     * Function to delay Center label at initial stage of accumulation chart.
     *
     * @param {Element} element - The element to delay.
     * @returns {void}
     */
    centerLabelDelay(element) {
        element.style.visibility = 'hidden';
        const animation = new Animation({});
        animation.animate(element, {
            delay: this.duration ? this.duration : this.series[0].animation.duration,
            progress: (args) => {
                args.element.style.visibility = 'visible';
            }
        });
    }
    renderSubTitle(options) {
        let maxWidth = 0;
        let titleWidth = 0;
        const padding = 10;
        const alignment = this.titleStyle.textAlignment;
        const subTitleElementSize = measureText(this.subTitle, this.subTitleStyle, this.themeStyle.chartSubTitleFont);
        for (const titleText of this.titleCollection) {
            titleWidth = measureText(titleText, this.titleStyle, this.themeStyle.chartSubTitleFont).width;
            maxWidth = titleWidth > maxWidth ? titleWidth : maxWidth;
        }
        const rect = new Rect(alignment === 'Center' ? (options.x - maxWidth / 2) : alignment === 'Far' ? options.x - maxWidth : options.x, 0, maxWidth, 0);
        if (this.titleStyle.position === 'Left') {
            rect.x = alignment === 'Center' ? (options.x - maxWidth * 0.5) : alignment === 'Far' ? this.margin.left + ((subTitleElementSize.height) * 3 / 4) : (options.x - maxWidth);
        }
        const subTitleOption = new TextOption(this.element.id + '_subTitle', titlePositionX(rect, this.subTitleStyle), options.y * options.text.length + ((subTitleElementSize.height) * 3 / 4) + padding, getTextAnchor(this.subTitleStyle.textAlignment, this.enableRtl), this.subTitleCollection, options.transform, 'auto');
        textElement(this.renderer, subTitleOption, this.subTitleStyle, this.subTitleStyle.color || this.themeStyle.chartSubTitleFont.color, this.svgObject, false, this.redraw, null, null, null, null, null, null, null, null, this.themeStyle.chartSubTitleFont);
    }
    /**
     * To get the series parent element.
     *
     * @private
     * @returns {Element} - The parent element of the series.
     */
    getSeriesElement() {
        return this.svgObject.getElementsByTagName('g')[0];
    }
    /**
     * To refresh the all visible series points.
     *
     * @private
     * @returns {void}
     */
    refreshSeries() {
        for (const series of this.visibleSeries) {
            this.refreshPoints(series.points);
        }
    }
    /**
     * To refresh points label region and visible.
     *
     * @private
     * @param {AccPoints[]} points - The array of points to refresh.
     * @returns {void}
     */
    refreshPoints(points) {
        for (const point of points) {
            point.labelPosition = null;
            point.labelRegion = null;
            point.labelVisible = true;
        }
    }
    /**
     * To get Module name.
     *
     * @private
     * @returns {string} - Returns the module name.
     */
    getModuleName() {
        return 'accumulationchart';
    }
    /**
     * To destroy the accumulation charts.
     *
     * @private
     * @returns {void}
     */
    destroy() {
        /**
         * To fix react timeout destroy issue.
         */
        if (this.element) {
            this.unWireEvents();
            super.destroy();
            this.element.classList.remove('e-accumulationchart');
            this.element.classList.remove('e-accumulationchart-focused');
            const element = document.getElementById(this.element.id + 'Keyboard_accumulationchart_focus');
            if (element) {
                element.remove();
            }
            removeElement('chartmeasuretext');
            this.removeSvg();
            const highlightElement = document.getElementById(this.element.id + '_ej2_chart_highlight');
            if (highlightElement) {
                highlightElement.remove();
            }
            const selectionElement = document.getElementById(this.element.id + '_ej2_chart_selection');
            if (selectionElement) {
                selectionElement.remove();
            }
            this.svgObject = null;
        }
    }
    /**
     * To provide the array of modules needed for control rendering.
     *
     * @returns {ModuleDeclaration[]} - required modules.
     * @private
     */
    requiredModules() {
        const modules = [];
        let enableAnnotation = false;
        modules.push({
            member: this.type + 'Series',
            args: [this]
        });
        if (this.legendSettings.visible) {
            modules.push({
                member: 'AccumulationLegend',
                args: [this]
            });
        }
        if (this.findDatalabelVisibility()) {
            modules.push({
                member: 'AccumulationDataLabel',
                args: [this]
            });
        }
        if (this.tooltip.enable) {
            modules.push({
                member: 'AccumulationTooltip',
                args: [this]
            });
        }
        if (this.selectionMode !== 'None') {
            modules.push({
                member: 'AccumulationSelection',
                args: [this]
            });
        }
        if (this.highlightMode !== 'None') {
            modules.push({
                member: 'AccumulationHighlight',
                args: [this]
            });
        }
        if (this.enableExport || this.allowExport) {
            modules.push({
                member: 'Export',
                args: [this]
            });
        }
        enableAnnotation = this.annotations.some((value) => {
            return (value.content !== null);
        });
        if (enableAnnotation) {
            modules.push({
                member: 'Annotation',
                args: [this]
            });
        }
        return modules;
    }
    /**
     * To find datalabel visibility in series.
     *
     * @returns {boolean} - false
     */
    findDatalabelVisibility() {
        for (const series of this.series) {
            if (series.dataLabel.visible) {
                return true;
            }
        }
        return false;
    }
    /**
     * Get visible series for accumulation chart by index.
     *
     * @param {AccumulationSeries[]} visibleSeries - The array of visible series in the accumulation chart.
     * @param {number} index - The index of the series to retrieve.
     * @returns {AccumulationSeries} - The visible series at the specified index.
     */
    changeVisibleSeries(visibleSeries, index) {
        for (const series of visibleSeries) {
            if (index === series.index) {
                return series;
            }
        }
        return null;
    }
    /**
     * Get the properties to be maintained in the persisted state.
     *
     * @private
     * @returns {string} - The persisted data containing the properties.
     */
    getPersistData() {
        return '';
    }
    /**
     * Method to sanitize any potentially untrusted strings and scripts before rendering them.
     *
     * @param {string} value - Specifies the html value to sanitize
     * @returns {string} Returns the sanitized html string
     * @private
     */
    sanitize(value) {
        if (this.enableHtmlSanitizer) {
            return SanitizeHtmlHelper.sanitize(value);
        }
        return value;
    }
    /**
     * Builds and registers a linear gradient for a series.
     *
     * @param {AccumulationSeries} series - The series to apply the gradient to
     * @param {LinearGradientModel} gradient - The linear gradient configuration
     * @param {AccPoints} point - The data point for gradient.
     * @param {number} pointIndex - Specifies the index of the point.
     * @returns {string} - The URL for the created gradient.
     *
     * @private
     */
    createLinearGradient(series, gradient, point, pointIndex) {
        const stops = normalizeGradientStops(gradient.gradientColorStop);
        const defs = getOrCreateSvgDefs(this.svgObject);
        const gradientId = !isNullOrUndefined(point) && !isNullOrUndefined(pointIndex)
            ? `${this.element.id}_series_${series.index}_point_${pointIndex}_linear_gradient`
            : `${this.element.id}_series_${series.index}_linear_gradient`;
        removePreviousGradient(defs, gradientId);
        const svgNS = 'http://www.w3.org/2000/svg';
        const linearGradient = document.createElementNS(svgNS, 'linearGradient');
        linearGradient.setAttribute('id', gradientId);
        linearGradient.setAttribute('gradientUnits', 'objectBoundingBox');
        linearGradient.setAttribute('x1', String(gradient.x1));
        linearGradient.setAttribute('y1', String(gradient.y1));
        linearGradient.setAttribute('x2', String(gradient.x2));
        linearGradient.setAttribute('y2', String(gradient.y2));
        for (const stop of stops) {
            const stopElement = document.createElementNS(svgNS, 'stop');
            stopElement.setAttribute('offset', `${stop.offset}%`);
            let adjustedColor = sanitizeGradientColor(stop.color);
            if (stop.lighten && stop.lighten > 0 && stop.lighten <= 1) {
                adjustedColor = lightenColor(adjustedColor, stop.lighten);
            }
            if (stop.brighten && stop.brighten >= -1 && stop.brighten !== 0 && stop.brighten <= 1) {
                adjustedColor = brightenColor(adjustedColor, stop.brighten);
            }
            stopElement.setAttribute('stop-color', adjustedColor);
            stopElement.setAttribute('stop-opacity', String(stop.opacity));
            linearGradient.appendChild(stopElement);
        }
        defs.appendChild(linearGradient);
        if (isNullOrUndefined(point)) {
            series.gradientId = gradientId;
        }
        return gradientId;
    }
    /**
     * Builds and registers a radial gradient for a series.
     *
     * @param {AccumulationSeries} series - The series to apply the gradient to
     * @param {RadialGradientModel} gradient - The radial gradient configuration
     * @param {AccPoints} point - The data point for gradient.
     * @param {number} pointIndex - Specifies the index of the point.
     * @returns {string} - The URL for the created gradient.
     *
     * @private
     */
    createRadialGradient(series, gradient, point, pointIndex) {
        const stops = normalizeGradientStops(gradient.gradientColorStop);
        const defs = getOrCreateSvgDefs(this.svgObject);
        const gradientId = !isNullOrUndefined(point) && !isNullOrUndefined(pointIndex)
            ? `${this.element.id}_series_${series.index}_point_${pointIndex}_radial_gradient`
            : `${this.element.id}_series_${series.index}_radial_gradient`;
        removePreviousGradient(defs, gradientId);
        const svgNS = 'http://www.w3.org/2000/svg';
        const radialGradient = document.createElementNS(svgNS, 'radialGradient');
        const fx = !isNullOrUndefined(gradient.fx) ? gradient.fx : gradient.cx;
        const fy = !isNullOrUndefined(gradient.fy) ? gradient.fy : gradient.cy;
        radialGradient.setAttribute('id', gradientId);
        radialGradient.setAttribute('gradientUnits', 'objectBoundingBox');
        radialGradient.setAttribute('cx', String(gradient.cx));
        radialGradient.setAttribute('cy', String(gradient.cy));
        radialGradient.setAttribute('fx', String(fx));
        radialGradient.setAttribute('fy', String(fy));
        radialGradient.setAttribute('r', String(gradient.r));
        for (const stop of stops) {
            const stopElement = document.createElementNS(svgNS, 'stop');
            stopElement.setAttribute('offset', `${stop.offset}%`);
            let adjustedColor = sanitizeGradientColor(stop.color);
            if (stop.lighten && stop.lighten > 0 && stop.lighten <= 1) {
                adjustedColor = lightenColor(adjustedColor, stop.lighten);
            }
            if (stop.brighten && stop.brighten >= -1 && stop.brighten !== 0 && stop.brighten <= 1) {
                adjustedColor = brightenColor(adjustedColor, stop.brighten);
            }
            stopElement.setAttribute('stop-color', adjustedColor);
            stopElement.setAttribute('stop-opacity', String(stop.opacity));
            radialGradient.appendChild(stopElement);
        }
        defs.appendChild(radialGradient);
        if (isNullOrUndefined(point)) {
            series.gradientId = gradientId;
        }
        return gradientId;
    }
    /**
     * Public wrapper method to create a linear gradient for an individual point.
     * Called from renderPoints in acc-base.ts.
     *
     * @param {AccumulationSeries} series - The series containing the point
     * @param {AccPoints} point - The specific point to apply gradient to
     * @param {number} pointIndex - The index of the point
     * @param {LinearGradientModel} gradient - The linear gardient for point.
     * @returns {string} The gradient ID
     *
     * @private
     */
    createPointLinearGradient(series, point, pointIndex, gradient) {
        const gradientToUse = point.linearGradient || gradient;
        if (!gradientToUse) {
            return '';
        }
        return this.createLinearGradient(series, gradientToUse, point, pointIndex);
    }
    /**
     * Public wrapper method to create a radial gradient for an individual point.
     * Called from renderPoints in acc-base.ts.
     *
     * @param {AccumulationSeries} series - The series containing the point
     * @param {AccPoints} point - The specific point to apply gradient to
     * @param {number} pointIndex - The index of the point
     * @param {RadialGradientModel} gradient - Specifies the radial grdient for the point.
     * @returns {string} The gradient ID
     *
     * @private
     */
    createPointRadialGradient(series, point, pointIndex, gradient) {
        const gradientToUse = point.radialGradient || gradient;
        if (!gradientToUse) {
            return '';
        }
        return this.createRadialGradient(series, gradientToUse, point, pointIndex);
    }
    /**
     * Applies gradient fills to the accumulation chart series and its individual points.
     *
     * @returns {void}
     */
    applyGradientsToSeries() {
        if (!this.svgObject || !this.visibleSeries || !this.visibleSeries.length) {
            return;
        }
        const series = this.visibleSeries[0];
        if (series.linearGradient && series.linearGradient.gradientColorStop &&
            series.linearGradient.gradientColorStop.length > 0) {
            this.createLinearGradient(series, series.linearGradient);
        }
        else if (series.radialGradient && series.radialGradient.gradientColorStop &&
            series.radialGradient.gradientColorStop.length > 0) {
            this.createRadialGradient(series, series.radialGradient);
        }
    }
    /**
     * Called internally if any of the property value changed.
     *
     * @private
     * @param {AccumulationChartModel} newProp - The new AccumulationChartModel.
     * @param {AccumulationChartModel} oldProp - The old AccumulationChartModel.
     * @returns {void}
     */
    onPropertyChanged(newProp, oldProp) {
        const update = {
            refreshElements: false, refreshBounds: false
        };
        for (const prop of Object.keys(newProp)) {
            switch (prop) {
                case 'theme':
                    this.animateSeries = true;
                    break;
                case 'title':
                case 'subTitle':
                case 'height':
                case 'width':
                case 'margin':
                    update.refreshBounds = true;
                    break;
                case 'titleStyle':
                    if (newProp.titleStyle && (newProp.titleStyle.size || newProp.titleStyle.textOverflow)) {
                        update.refreshBounds = true;
                    }
                    else {
                        update.refreshElements = true;
                    }
                    break;
                case 'subTitleStyle':
                    if (newProp.subTitleStyle && (newProp.subTitleStyle.size || newProp.subTitleStyle.textOverflow)) {
                        update.refreshBounds = true;
                    }
                    else {
                        update.refreshElements = true;
                    }
                    break;
                case 'legendSettings':
                    update.refreshBounds = true;
                    update.refreshElements = true;
                    break;
                case 'dataSource':
                    this.processData(false);
                    update.refreshBounds = true;
                    break;
                case 'series':
                    if (!this.animateselected) {
                        const len = this.series.length;
                        let seriesRefresh = false;
                        let series;
                        for (let i = 0; i < len; i++) {
                            series = newProp.series[i];
                            if (newProp.series[i] && (newProp.series[i].dataSource || newProp.series[i].yName
                                || newProp.series[i].xName || series.type || newProp.series[i].legendShape ||
                                newProp.series[i].dataLabel || series.radius || series.innerRadius ||
                                series.startAngle || series.endAngle || series.gapRatio || series.neckWidth || series.explode ||
                                series.neckWidth || series.pyramidMode || series.explodeOffset || series.funnelMode ||
                                series.linearGradient || series.radialGradient)) {
                                extend(this.changeVisibleSeries(this.visibleSeries, i), series, null, true);
                                seriesRefresh = true;
                            }
                            if (newProp.series[i] && !isNullOrUndefined(newProp.series[i].explodeIndex) &&
                                newProp.series[i].explodeIndex >= 0
                                && newProp.series[i].explodeIndex !== oldProp.series[i].explodeIndex) {
                                this.accBaseModule.explodePoints(newProp.series[i].explodeIndex, this);
                                this.accBaseModule.deExplodeAll(newProp.series[i].explodeIndex, this.enableAnimation ? 300 : 0);
                            }
                            else if (newProp.series[i].explodeIndex < 0) {
                                this.accBaseModule.deExplodeAll(newProp.series[i].explodeIndex, this.enableAnimation ? 300 : 0);
                            }
                            if (!this.pieSeriesModule) {
                                this.pieSeriesModule = new PieSeries(this);
                            }
                        }
                        if (seriesRefresh) {
                            this.calculateVisibleSeries();
                            this.processData(false);
                            update.refreshBounds = true;
                        }
                    }
                    this.animateselected = false;
                    this.redraw = false;
                    break;
                case 'enableRtl':
                case 'locale':
                case 'currencyCode':
                    super.refresh();
                    break;
                case 'background':
                case 'border':
                case 'annotations':
                case 'enableSmartLabels':
                    update.refreshElements = true;
                    break;
                case 'isMultiSelect':
                case 'selectedDataIndexes':
                case 'selectionMode':
                    if (this.accumulationSelectionModule) {
                        if (isNullOrUndefined(this.accumulationSelectionModule.selectedDataIndexes)) {
                            this.accumulationSelectionModule.invokeSelection(this);
                        }
                        else {
                            this.accumulationSelectionModule.selectedDataIndexes = this.selectedDataIndexes;
                            this.accumulationSelectionModule.redrawSelection(this);
                        }
                    }
                    break;
                case 'tooltip':
                    if (this.accumulationTooltipModule) { // To check the tooltip enable is true.
                        this.accumulationTooltipModule.previousPoints = [];
                        if (this.tooltip.template) {
                            this.accumulationTooltipModule.template = this.tooltip.template;
                        }
                    }
                    break;
                case 'center':
                    if (!isNullOrUndefined(newProp.center.x)) {
                        this.center.x = newProp.center.x;
                        update.refreshElements = true;
                    }
                    if (!isNullOrUndefined(newProp.center.y)) {
                        this.center.y = newProp.center.y;
                        update.refreshElements = true;
                    }
                    break;
            }
        }
        if (!update.refreshBounds && update.refreshElements) {
            this.createPieSvg();
            this.renderElements();
        }
        else if (update.refreshBounds) {
            this.refreshSeries();
            this.createPieSvg();
            this.calculateBounds();
            this.renderElements();
        }
    }
};
__decorate([
    Property(null)
], AccumulationChart.prototype, "noDataTemplate", void 0);
__decorate([
    Property(null)
], AccumulationChart.prototype, "width", void 0);
__decorate([
    Property(null)
], AccumulationChart.prototype, "height", void 0);
__decorate([
    Property(null)
], AccumulationChart.prototype, "title", void 0);
__decorate([
    Property(null)
], AccumulationChart.prototype, "backgroundImage", void 0);
__decorate([
    Complex({}, PieCenter)
], AccumulationChart.prototype, "center", void 0);
__decorate([
    Property('')
], AccumulationChart.prototype, "dataSource", void 0);
__decorate([
    Complex({ fontFamily: null, size: null, fontStyle: null, fontWeight: null, color: null }, TitleStyleSettings)
], AccumulationChart.prototype, "titleStyle", void 0);
__decorate([
    Property(null)
], AccumulationChart.prototype, "subTitle", void 0);
__decorate([
    Complex({ fontFamily: null, size: null, fontStyle: null, fontWeight: null, color: null }, TitleStyleSettings)
], AccumulationChart.prototype, "subTitleStyle", void 0);
__decorate([
    Complex({}, LegendSettings)
], AccumulationChart.prototype, "legendSettings", void 0);
__decorate([
    Complex({}, TooltipSettings)
], AccumulationChart.prototype, "tooltip", void 0);
__decorate([
    Complex({}, CenterLabel)
], AccumulationChart.prototype, "centerLabel", void 0);
__decorate([
    Property('None')
], AccumulationChart.prototype, "selectionMode", void 0);
__decorate([
    Property('')
], AccumulationChart.prototype, "highlightColor", void 0);
__decorate([
    Property('None')
], AccumulationChart.prototype, "highlightMode", void 0);
__decorate([
    Property('None')
], AccumulationChart.prototype, "selectionPattern", void 0);
__decorate([
    Property('None')
], AccumulationChart.prototype, "highlightPattern", void 0);
__decorate([
    Property(false)
], AccumulationChart.prototype, "enableHtmlSanitizer", void 0);
__decorate([
    Property(true)
], AccumulationChart.prototype, "enableBorderOnMouseMove", void 0);
__decorate([
    Property(false)
], AccumulationChart.prototype, "isMultiSelect", void 0);
__decorate([
    Property(true)
], AccumulationChart.prototype, "enableAnimation", void 0);
__decorate([
    Collection([], Indexes)
], AccumulationChart.prototype, "selectedDataIndexes", void 0);
__decorate([
    Complex({}, Margin)
], AccumulationChart.prototype, "margin", void 0);
__decorate([
    Property(true)
], AccumulationChart.prototype, "enableSmartLabels", void 0);
__decorate([
    Complex({ color: '#DDDDDD', width: 0 }, Border)
], AccumulationChart.prototype, "border", void 0);
__decorate([
    Property(null)
], AccumulationChart.prototype, "background", void 0);
__decorate([
    Collection([{}], AccumulationSeries)
], AccumulationChart.prototype, "series", void 0);
__decorate([
    Collection([{}], AccumulationAnnotationSettings)
], AccumulationChart.prototype, "annotations", void 0);
__decorate([
    Property('Material')
], AccumulationChart.prototype, "theme", void 0);
__decorate([
    Property(false)
], AccumulationChart.prototype, "useGroupingSeparator", void 0);
__decorate([
    Property(true)
], AccumulationChart.prototype, "enableExport", void 0);
__decorate([
    Property(false)
], AccumulationChart.prototype, "allowExport", void 0);
__decorate([
    Complex({}, Accessibility)
], AccumulationChart.prototype, "accessibility", void 0);
__decorate([
    Property(null)
], AccumulationChart.prototype, "focusBorderColor", void 0);
__decorate([
    Property(1.5)
], AccumulationChart.prototype, "focusBorderWidth", void 0);
__decorate([
    Property(0)
], AccumulationChart.prototype, "focusBorderMargin", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "loaded", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "legendClick", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "load", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "seriesRender", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "legendRender", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "textRender", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "tooltipRender", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "pointRender", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "annotationRender", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "beforePrint", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "chartMouseMove", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "chartMouseClick", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "chartDoubleClick", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "pointClick", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "pointMove", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "animationComplete", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "chartMouseDown", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "chartMouseLeave", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "chartMouseUp", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "beforeResize", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "resized", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "beforeExport", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "afterExport", void 0);
__decorate([
    Event()
], AccumulationChart.prototype, "selectionComplete", void 0);
__decorate([
    Property('USD')
], AccumulationChart.prototype, "currencyCode", void 0);
AccumulationChart = __decorate([
    NotifyPropertyChanges
], AccumulationChart);
export { AccumulationChart };
